import classes from "../BearingFrequencyCalculator.module.less";
import { Controller, useForm } from "react-hook-form";
import Radio from "../../common/components/Radio";
import CustomNumberFormat from "../../common/components/CustomNumberFormat";
import { CloseBtn } from "../../AddToCart/components/addToCart/AtcCloseButton";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { LoadingButton } from "../../common/components/Button/Button";
import { useState } from "react";
import { type BearingFrequencyCalculatorForm } from "./BearingFrequencyCalculator";
import {
  type BearingFrequencyCalculationParams,
  type BearingFrequencyCalculationResult,
  calculateBearingFrequency,
} from "../calculateBearingFrequency";
import { trackBearing } from "~/feature/Analytics/ga/ga";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";

interface FormProps {
  handleChange: (values: BearingFrequencyCalculationResult | null) => void;
}

export function FormBFC({ handleChange }: FormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<BearingFrequencyCalculatorForm>({
    defaultValues: {
      isInch: false,
      pitchDiameter: null,
      rollingDiameter: null,
      isDeg: true,
      contactAngle: null,
      rollingElementAmount: null,
      speed: 60,
    },
    mode: "onChange",
  });

  const onSubmitHandler = handleSubmit((values) => {
    handleChange(null);
    setIsLoading(true);
    if (!isApplyDisabled) {
      const result = calculateBearingFrequency(values as BearingFrequencyCalculationParams);
      if (result) {
        trackBearing("click_on_apply");
        handleChange(result);
      }
    }
    setIsLoading(false);
  });

  const pitchDiameter = watch("pitchDiameter");
  const rollingDiameter = watch("rollingDiameter");
  const contactAngle = watch("contactAngle");
  const rollingElementAmount = watch("rollingElementAmount");
  const speed = watch("speed");
  const isDeg = watch("isDeg");
  const isInch = watch("isInch");

  const PI = Math.PI;
  const angleValue = contactAngle && (isDeg ? (contactAngle * PI) / 180 : contactAngle);
  const dt = pitchDiameter && (isInch ? pitchDiameter * 25.4 : pitchDiameter);
  const dw = rollingDiameter && (isInch ? rollingDiameter * 25.4 : rollingDiameter);

  const errorDiameter = pitchDiameter && rollingDiameter ? pitchDiameter < rollingDiameter : false;
  const errorRel = dw && dt && rollingElementAmount ? PI / rollingElementAmount < Math.asin(dw / dt) : false;
  const errorAngle = angleValue ? angleValue <= 0 || angleValue >= PI / 2 : false;

  const isApplyDisabled =
    !(
      !!pitchDiameter &&
      !!rollingDiameter &&
      (!!contactAngle || !(contactAngle && contactAngle >= 0)) &&
      !!rollingElementAmount &&
      !!speed
    ) ||
    errorDiameter ||
    errorRel ||
    errorAngle;

  return (
    <div className={classes.form}>
      <div className={classes.formTitle}>{t("bearingFrequencyCalculator.form.title")}</div>
      {errorDiameter && <Alert severity="error">{t("bearingFrequencyCalculator.error.diameter")}</Alert>}
      {errorRel && <Alert severity="error">{t("bearingFrequencyCalculator.error.relationship")}</Alert>}
      {errorAngle && <Alert severity="error">{t("bearingFrequencyCalculator.error.angle")}</Alert>}
      <img
        className={classes.formItemImageMobile}
        src="https://cdn.schaeffler-ecommerce.com/cdn/BFCMobile.png"
        alt=""
      />
      <div className={classes.section}>
        <Controller
          control={control}
          name={"isInch"}
          render={({ field: { value, onChange } }) => (
            <div className={"scha__radio-switch__options"}>
              <div className={"scha__radio-switch__option"} onClick={() => onChange(false)}>
                <Radio active={value === false} className={"icon-margin"} />
                <span>{t("bearingFrequencyCalculator.form.mm")}</span>
              </div>
              <div className={"scha__radio-switch__option"} onClick={() => onChange(true)}>
                <Radio active={value === true} className={"icon-margin"} />
                <span>{t("bearingFrequencyCalculator.form.inch")}</span>
              </div>
            </div>
          )}
        />

        <div className={classes.formRow}>
          <Controller
            control={control}
            name={"pitchDiameter"}
            rules={{
              required: true,
              validate: (value) => (value && value > 0) || `${t("bearingFrequencyCalculator.error.greaterThan")}`,
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: `${t("bearingFrequencyCalculator.error.numeric")}`,
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className={classes.formItem}>
                <img
                  className={classes.formItemImage}
                  src="https://cdn.schaeffler-ecommerce.com/cdn/0019E1D4_s04_3c_0k_0k.svg"
                  alt=""
                />
                <CustomNumberFormat
                  className={classes.formItemInput}
                  label={t("bearingFrequencyCalculator.form.pitchDiameter")}
                  value={value}
                  handleState={(e) => onChange(e.floatValue)}
                  error={!!error}
                  helperText={error?.message}
                />
              </div>
            )}
          />

          <Controller
            control={control}
            name={"rollingDiameter"}
            rules={{
              required: true,
              validate: (value) => (value && value > 0) || `${t("bearingFrequencyCalculator.error.greaterThan")}`,
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: `${t("bearingFrequencyCalculator.error.numeric")}`,
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className={classes.formItem}>
                <img
                  className={classes.formItemImage}
                  src="https://cdn.schaeffler-ecommerce.com/cdn/0019E1D7_s04_3c_0k_0k.svg"
                  alt=""
                />
                <CustomNumberFormat
                  className={classes.formItemInput}
                  label={t("bearingFrequencyCalculator.form.rollingDiameter")}
                  value={value}
                  handleState={(e) => onChange(e.floatValue)}
                  error={!!error}
                  helperText={error?.message}
                />
              </div>
            )}
          />
        </div>
      </div>
      <div className={classes.section}>
        <Controller
          control={control}
          name={"isDeg"}
          render={({ field: { value, onChange } }) => (
            <div className={"scha__radio-switch__options"}>
              <div className={"scha__radio-switch__option"} onClick={() => onChange(true)}>
                <Radio active={value === true} className={"icon-margin"} />
                <span>{t("bearingFrequencyCalculator.form.deg")}</span>
              </div>
              <div className={"scha__radio-switch__option"} onClick={() => onChange(false)}>
                <Radio active={value === false} className={"icon-margin"} />
                <span>{t("bearingFrequencyCalculator.form.rad")}</span>
              </div>
            </div>
          )}
        />

        <div className={classes.formRow}>
          <Controller
            control={control}
            name={"contactAngle"}
            rules={{
              required: true,
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: `${t("bearingFrequencyCalculator.error.numeric")}`,
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className={classes.formItem}>
                <img
                  className={classes.formItemImage}
                  src="https://cdn.schaeffler-ecommerce.com/cdn/0019E1D9_s04_3c_0k_0k.svg"
                  alt=""
                />
                <CustomNumberFormat
                  className={classes.formItemInput}
                  label={t("bearingFrequencyCalculator.form.contactAngle")}
                  value={value}
                  handleState={(e) => onChange(e.floatValue)}
                  error={!!error}
                  helperText={error?.message}
                />
              </div>
            )}
          />
        </div>
      </div>
      <div className={classes.section}>
        <div className={classes.formRow}>
          <Controller
            control={control}
            name={"rollingElementAmount"}
            rules={{
              required: true,
              min: { value: 3, message: `${t("bearingFrequencyCalculator.error.rolling")}` },
              pattern: { value: /^\d+$/, message: `${t("bearingFrequencyCalculator.error.rollingWhole")}` },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomNumberFormat
                className={classes.formItemInput}
                label={t("bearingFrequencyCalculator.form.numRollingEle")}
                value={value}
                handleState={(e) => onChange(e.floatValue)}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"speed"}
            rules={{
              required: true,
              min: 0,
              pattern: {
                value: /^\d+(?:\.\d{1,2})?$/,
                message: `${t("bearingFrequencyCalculator.error.numeric")}`,
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CustomNumberFormat
                className={classes.formItemInput}
                label={t("bearingFrequencyCalculator.form.speed")}
                value={value}
                handleState={(e) => {
                  e.floatValue !== undefined ? onChange(e.floatValue) : onChange(null);
                }}
                error={!!error}
                helperText={error?.message ? error.message : t("bearingFrequencyCalculator.form.speedHelper")}
              />
            )}
          />
        </div>
      </div>
      <div className={classes.formButtonSection}>
        <CloseBtn
          data-test-id=""
          onClick={() => {
            reset();
            handleChange(null);
          }}
        >
          <Icon type={IconType.Reset} className="icon-margin" />
          {t("bearingFrequencyCalculator.form.resetAll")}
        </CloseBtn>

        <LoadingButton
          data-test-id=""
          onClick={() => onSubmitHandler()}
          disabled={isApplyDisabled}
          inFlight={isLoading}
        >
          {t("bearingFrequencyCalculator.form.apply")}
        </LoadingButton>
      </div>
    </div>
  );
}
